import { MMButton, MMCheckbox, MMLabelValue, PreviewLayout, TrustedRecipientCheckbox } from '@components'
import { RecurringPreviewRow } from '@components/RecurringPreviewRow'
import { getAccountingCategoryLabel } from '@constants'
import { useFeatureFlag, useWithdrawPromoCode } from '@hooks'
import AmountPreviewRow from '@withdrawals/components/AmountPreviewRow'
import FxRateRow from '@withdrawals/components/FxRateRow'
import FxRateTimerRow from '@withdrawals/components/FxRateTimerRow'
import MainAmountRow from '@withdrawals/components/MainAmountRow/MainAmountRow'
import styles from '@withdrawals/styles/WithdrawPreview.module.scss'
import {
  useBankTransferPreviewQry,
  useFeatureFlagState,
  useMyAccountAccessQry,
  useWithdrawPreviewMutation,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse, RecipientResponse, WithdrawalSubFlow } from 'mmfintech-backend-api/types'
import { formatIban, tr, translateCountryName } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useState } from 'react'

type BankTransferPreviewProps = {
  formValues: UseFormValuesOutput
  selectedAccount: AccountBalanceResponse
  selectedRecipient: RecipientResponse
  loading: boolean
  onSubmit: () => void
  onCancel: () => void
  /** Optional callback when promo code is applied - can be sync or async */
  onApplyPromoCode?: (code: string) => void | Promise<void>
  /** Document IDs for third-party withdrawals */
  transactionDocumentIds?: number[]
  /** Whether the transfer is to own account */
  ownAccount?: boolean
  subFlow: WithdrawalSubFlow
  sepaInstantEnabled?: boolean
}

const BankTransferPreview = (props: BankTransferPreviewProps) => {
  const {
    formValues,
    selectedAccount,
    selectedRecipient,
    loading,
    onSubmit,
    onCancel,
    onApplyPromoCode,
    transactionDocumentIds,
    ownAccount,
    subFlow,
    sepaInstantEnabled
  } = props

  const userSettingsInitiatorPermissions = useFeatureFlag('userSettingsInitiatorPermissions')

  const sepaInstantFF = useFeatureFlagState('SEPA_INSTANT')
  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  const { hasRequestPermission, hasWritePermission } = useMyAccountAccessQry()

  const hasWithdrawPromoCode = useFeatureFlag('withdrawPromoCode')

  // RTK Query - source of truth for API data
  const { transferPreview, transferError, transferFetching } = useBankTransferPreviewQry()

  // Track promo code state for clear functionality
  const [promoCode, setPromoCode] = useState('')

  // Promo code validation hook (combines stable preview + error handling + validation logic)
  const {
    displayPreview,
    validatePromoCode,
    isLoading: isPromoLoading,
    errorMessage: promoErrorMessage
  } = useWithdrawPromoCode({
    mutation: useWithdrawPreviewMutation,
    subFlow: WithdrawalSubFlows.BANKWIRE,
    previewData: transferPreview,
    buildRequest: (code: string) => {
      const allFormValues = formValues.prepare()
      return {
        ...allFormValues,
        amount: parseFloat(allFormValues.amount),
        sourceAccountId: selectedAccount?.id,
        destinationPaymentInstrumentId: selectedRecipient?.paymentInstrumentId,
        promoCode: code,
        ...(transactionDocumentIds?.length ? { transactionDocumentIds } : null),
        ...(typeof ownAccount === 'boolean' ? { ownAccount } : null)
      }
    },
    onSuccess: async (code: string) => {
      setPromoCode(code)
      if (onApplyPromoCode) {
        await onApplyPromoCode(code)
      }
    }
  })

  const handleClearPromoCode = async () => {
    try {
      await validatePromoCode('')
    } finally {
      setPromoCode('')
    }
  }

  // Use displayPreview for all UI rendering (stable, won't nullify on errors)
  const {
    fee,
    feeCurrency,
    processingAmount,
    processingCurrency,
    walletAmount,
    walletCurrency,
    trustedRecipient,
    overdraftAmount,
    originalFeeAmount,
    promoCodeDiscount
  } = displayPreview || {}

  const isRequestMode = hasRequestPermission(selectedAccount?.id) && !hasWritePermission(selectedAccount?.id)
  const showPromo = hasWithdrawPromoCode && !!onApplyPromoCode && !isRequestMode && Number(originalFeeAmount || fee) > 0
  const isPromoApplied = !!promoCode && Number(promoCodeDiscount) > 0

  return (
    <PreviewLayout
      showPromo={showPromo}
      promoCodeConfig={{
        validatePromoCode,
        isLoading: isPromoLoading,
        error: promoErrorMessage,
        isApplied: isPromoApplied,
        onClear: handleClearPromoCode
      }}
    >
      <div className={styles['withdraw-preview-wrapper']}>
        <div className='withdraw-preview-container amount-container'>
          <MainAmountRow
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.AMOUNT', 'Amount')}
            amount={walletAmount}
            currency={walletCurrency}
          />
          <AmountPreviewRow
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.OVERDRAFT', 'Overdraft amount')}
            amount={overdraftAmount}
            currency={processingCurrency}
            data-test='overdraft-amount'
          />
          <AmountPreviewRow
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.FEE', 'Fee')}
            amount={originalFeeAmount || fee}
            currency={feeCurrency}
            discountedAmount={fee}
            showDiscount={!!originalFeeAmount && originalFeeAmount !== fee}
          />
          <AmountPreviewRow
            label={tr('FRONTEND.PROMO.DISCOUNT_SAVING', 'Discount saving')}
            amount={promoCodeDiscount}
            currency={feeCurrency}
            className='promo'
            data-test='discount'
          />
          {subFlow === WithdrawalSubFlows.MASTERCARD && (
            <FxRateRow
              label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.RATE', 'Rate')}
              currency={processingCurrency}
              fxRate={displayPreview?.fxRate}
              fxRateCurrency={displayPreview?.fxRateCurrency}
            />
          )}
          {subFlow === WithdrawalSubFlows.MASTERCARD && (
            <FxRateTimerRow quoteExpirationDate={displayPreview?.quoteExpirationDate} onTimeout={onCancel} />
          )}
          <AmountPreviewRow
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.NET_AMOUNT', 'Net amount')}
            amount={processingAmount}
            currency={processingCurrency}
            data-test='net-amount'
          />
        </div>

        <div className='withdraw-preview-container send-money-details'>
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.SAVINGS.INVEST.ACCOUNT', 'From account')}
            value={selectedAccount?.name}
            data-test='account-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.TO', 'To')}
            value={selectedRecipient?.holderName || selectedRecipient?.name}
            data-test='recipient-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.COUNTRY', 'Country')}
            value={translateCountryName(selectedRecipient?.country)}
            data-test='country'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.IBAN', 'IBAN')}
            value={formatIban(selectedRecipient?.iban)}
            data-test='iban'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BANK_CODE', 'Bank code')}
            value={selectedRecipient?.bankCode}
            data-test='bank-code'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BANK_NAME', 'Bank name')}
            value={selectedRecipient?.bankName}
            data-test='bank-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BRANCH_CODE', 'Branch code')}
            value={selectedRecipient?.branchCode}
            data-test='branch-code'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BRANCH_NAME', 'Branch name')}
            value={selectedRecipient?.branchName}
            data-test='branch-name'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.ACCOUNT_NUMBER', 'Account number')}
            value={selectedRecipient?.accountNumber}
            data-test='account-number'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.SWIFT', 'SWIFT')}
            value={selectedRecipient?.bicSwift}
            data-test='swift'
          />
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.REFERENCE', 'Reference')}
            value={formValues.getValue('reference')}
            data-test='reference'
          />

          {accountingCategoryFF.isEnabled && (
            <MMLabelValue
              variant='withdrawal'
              label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.CATEGORY', 'Payment category')}
              value={getAccountingCategoryLabel(formValues.getValue('accountingCategory'))}
              data-test='accounting-category'
            />
          )}

          <RecurringPreviewRow formValues={formValues} />
        </div>

        {sepaInstantFF.isEnabled && Boolean(sepaInstantEnabled) && (
          <div className='sepa-instant-checkbox'>
            <MMCheckbox
              checked={Boolean(formValues.getValue('sepaInstant'))}
              onClick={checked => formValues.setValue('sepaInstant', checked)}
              label={tr('FRONTEND.BANK_TRANSFER.PREVIEW.SEPA_INSTANT', 'SEPA Instant')}
            />
          </div>
        )}

        <TrustedRecipientCheckbox formValues={formValues} trustedRecipient={trustedRecipient} />

        <PreviewLayout.MobilePromo />

        <ErrorDisplay error={transferError} />

        <div className='preview-actions'>
          <MMButton
            type='button'
            size='large'
            variant='alternative'
            caption={
              userSettingsInitiatorPermissions && isRequestMode
                ? tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BUTTON_REQUEST', 'Request')
                : tr('FRONTEND.WITHDRAW.BANK_TRANSFER.PREVIEW.BUTTON_SEND', 'Send')
            }
            onClick={onSubmit}
            loading={transferFetching}
            disabled={loading}
            data-test='button-send'
            fullWidth
          />
          <MMButton
            type='button'
            size='large'
            variant='tertiary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onCancel}
            disabled={loading || transferFetching}
            data-test='button-cancel'
            fullWidth
          />
        </div>
      </div>
    </PreviewLayout>
  )
}

export default BankTransferPreview
